<div class="rsa-id-verification-report">
  <!-- Page Control for export of data -->
  <PageControl
    :title="'RSA ID Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      @click="show_export_modal"
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
    >
      <i class="bi bi-download"></i>
      Export Data
    </button>
  </PageControl>

  <!-- Export Modal - Teleported to body to prevent z-index issues -->
  <Teleport to="body">
    <div
      class="modal fade"
      :id="'export-modal-' + report_reference"
      tabindex="-1"
      aria-labelledby="exportModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export RSA ID Verification Report Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Select Data to Export:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.personal_information"
                  id="personalInformation"
                />
                <label class="form-check-label" for="personalInformation">
                  Personal Information
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.document_details"
                  id="documentDetails"
                />
                <label class="form-check-label" for="documentDetails">
                  Document Details
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.verification_results"
                  id="verificationResults"
                />
                <label class="form-check-label" for="verificationResults">
                  Verification Results
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.system_status"
                  id="systemStatus"
                />
                <label class="form-check-label" for="systemStatus">
                  System Status Information
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.raw_json"
                  id="rawJson"
                />
                <label class="form-check-label" for="rawJson">
                  Raw JSON Response Data
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Export Format:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="csv"
                  id="csvFormat"
                />
                <label class="form-check-label" for="csvFormat">
                  CSV (Separate files for each table)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="excel"
                  id="excelFormat"
                />
                <label class="form-check-label" for="excelFormat">
                  Excel (Multiple sheets in one file)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="json"
                  id="jsonFormat"
                />
                <label class="form-check-label" for="jsonFormat">
                  JSON (JavaScript Object Notation)
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="close_modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              @click="export_data"
              :disabled="!has_selected_tables"
            >
              <i class="bi bi-download me-1"></i>
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <PageComponent>
    <SectionSheet>
      <div
        class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h4 fw-bold mb-1">RSA ID Verification</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">RSA ID Verification Summary</h6>
        </div>
        <div class="card-body text-xs">
          <p>
            This RSA ID verification report presents the results for
            <span class="fw-bold text-dark">{{ full_name }}</span>, ID Number
            <span class="fw-bold text-dark">{{ id_number }}</span>.
            The verification process utilizes the South African Home Affairs database 
            to validate the identity information provided against official government records. 
            The process verifies the authenticity of the ID number, confirms the person's 
            identity details, and checks various status indicators in the national database.
          </p>
          
          <!-- Status Cards -->
          <div class="row g-2">
            <div class="col-md-3">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-check"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Verification Status</div>
                  <div
                    class="status-value-simple bg-white"
                    :class="verification_status_class"
                  >
                    {{ overall_status_text }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-card-checklist"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">ID Status</div>
                  <div
                    class="status-value-simple bg-white"
                    :class="id_blocked_class"
                  >
                    {{ id_blocked ? 'Blocked' : 'Active' }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-database-check"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">HANIS Status</div>
                  <div
                    class="status-value-simple bg-white"
                    :class="on_hanis_class"
                  >
                    {{ on_hanis_text }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-people"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">NPR Status</div>
                  <div
                    class="status-value-simple bg-white"
                    :class="on_npr_class"
                  >
                    {{ on_npr_text }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Personal Information</h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <!-- First Name -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">First Name</div>
                  <div class="status-value-simple bg-white">
                    {{ first_name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Last Name -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-vcard"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Last Name</div>
                  <div class="status-value-simple bg-white">
                    {{ last_name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- ID Number -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-hash"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">ID Number</div>
                  <div class="status-value-simple bg-white">
                    {{ id_number }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Date of Birth -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-calendar-date"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Date of Birth</div>
                  <div class="status-value-simple bg-white">
                    {{ date_of_birth }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Age -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-hourglass-split"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Age</div>
                  <div class="status-value-simple bg-white">
                    {{ age }} years
                  </div>
                </div>
              </div>
            </div>

            <!-- Gender -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-gender-ambiguous"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Gender</div>
                  <div class="status-value-simple bg-white">
                    {{ gender }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Marital Status -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-heart"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Marital Status</div>
                  <div class="status-value-simple bg-white">
                    {{ marital_status }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Citizenship Status -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-flag"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Citizenship</div>
                  <div class="status-value-simple bg-white">
                    {{ citizenship_status }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Birth Place -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-geo-alt"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Birth Place</div>
                  <div class="status-value-simple bg-white">
                    {{ birth_place }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Dead Indicator -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-heart-pulse"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Deceased Status</div>
                  <div class="status-value-simple bg-white" :class="dead_indicator_class">
                    {{ dead_indicator_text }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Document Details and Verification -->
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Document Details & Verification</h6>
        </div>
        <div class="card-body">
          <!-- Official Photo Section -->
          <div class="row g-3 mb-4" v-if="facial_image_available">
            <div class="col-md-12">
              <div class="text-center">
                <h6 class="mb-3">Official ID Photo</h6>
                <div class="image-container mx-auto" style="max-width: 300px;">
                  <img
                    v-if="facial_image_src"
                    :src="facial_image_src"
                    alt="Official ID Photo"
                    class="verification-image"
                  />
                                    <div v-else class="no-image">
                    <i class="bi bi-image"></i>
                    <p>No facial image available</p>
                  </div>
                </div>
                <div class="mt-2 text-muted text-xs">
                  Official photo from Home Affairs database
                </div>
              </div>
            </div>
          </div>

          <!-- Document Information Cards -->
          <div class="row g-3">
            <!-- Smart Card Status -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-credit-card"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Smart Card</div>
                  <div class="status-value-simple bg-white">
                    {{ smart_card_status }}
                  </div>
                </div>
              </div>
            </div>

            <!-- ID Issue Date -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-calendar-event"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">ID Issue Date</div>
                  <div class="status-value-simple bg-white">
                    {{ formatted_id_issue_date }}
                  </div>
                </div>
              </div>
            </div>

            <!-- ID Sequence Number -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-123"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">ID Sequence Number</div>
                  <div class="status-value-simple bg-white">
                    {{ id_sequence_number }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Tracking Number -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-fingerprint"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">HANIS Tracking Number</div>
                  <div class="status-value-simple bg-white text-xs">
                    {{ tracking_number }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Verification Results Table -->
          <h6 class="mt-4 mb-3 border-bottom pb-2">
            <i class="bi bi-check-square me-2"></i>Verification Results
          </h6>
          <div class="table-responsive">
            <table class="table table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>Verification Check</th>
                  <th>Result</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <i class="bi bi-person me-2 text-secondary"></i>First Name Match
                  </td>
                  <td>
                    <span class="badge" :class="first_name_match_class">
                      {{ first_name_result }}%
                    </span>
                  </td>
                  <td>
                    {{ parseFloat(first_name_result) >= 80 ? 'Pass' : 'Fail' }}
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-person-vcard me-2 text-secondary"></i>Last Name Match
                  </td>
                  <td>
                    <span class="badge" :class="last_name_match_class">
                      {{ last_name_result }}%
                    </span>
                  </td>
                  <td>
                    {{ parseFloat(last_name_result) >= 80 ? 'Pass' : 'Fail' }}
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-shield-check me-2 text-secondary"></i>Overall Verification
                  </td>
                  <td>
                    <span class="badge" :class="overall_status_class">
                      {{ overall_status_text }}
                    </span>
                  </td>
                  <td>
                    {{ overall_status === 'success' ? 'Pass' : 'Fail' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- System Status Information -->
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">System Status & Additional Information</h6>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>System Check</th>
                  <th>Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <i class="bi bi-database me-2 text-secondary"></i>HANIS Database
                  </td>
                  <td>
                    <span class="badge" :class="on_hanis ? 'bg-success' : 'bg-warning'">
                      {{ on_hanis_text }}
                    </span>
                  </td>
                  <td>Home Affairs National Identification System</td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-people me-2 text-secondary"></i>NPR Database
                  </td>
                  <td>
                    <span class="badge" :class="on_npr ? 'bg-success' : 'bg-warning'">
                      {{ on_npr_text }}
                    </span>
                  </td>
                  <td>National Population Register</td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-shield-x me-2 text-secondary"></i>ID Blocked Status
                  </td>
                  <td>
                    <span class="badge" :class="id_blocked ? 'bg-danger' : 'bg-success'">
                      {{ id_blocked ? 'Blocked' : 'Not Blocked' }}
                    </span>
                  </td>
                  <td>Indicates if the ID is blocked or restricted</td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-heart-pulse me-2 text-secondary"></i>Deceased Indicator
                  </td>
                  <td>
                    <span class="badge" :class="dead_indicator ? 'bg-danger' : 'bg-success'">
                      {{ dead_indicator_text }}
                    </span>
                  </td>
                  <td>
                    <span v-if="dead_indicator && date_of_death">
                      Date of Death: {{ formatted_date_of_death }}
                    </span>
                    <span v-else>Person is not recorded as deceased</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-clock-history me-2 text-secondary"></i>Result Type
                  </td>
                  <td>
                    <span class="badge bg-info">
                      {{ cached_result ? 'Cached' : 'Live' }}
                    </span>
                  </td>
                  <td>
                    <span v-if="cached_result && cache_date">
                      Cached on: {{ formatted_cache_date }}
                    </span>
                    <span v-else>Real-time verification result</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Additional Details -->
          <div class="row g-3 mt-3">
            <div class="col-md-12">
              <div class="alert alert-info text-xs">
                <i class="bi bi-info-circle me-2"></i>
                <strong>Verification Message:</strong> {{ message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>

    <!-- Disclaimer Section on its own page -->
    <SectionSheet>
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header bg-light">
          <h6 class="mb-0">Disclaimer and Information</h6>
        </div>
        <div class="card-body">
          <div class="disclaimer-content">
            <p class="text-xs mb-3">
              This RSA ID Verification Report provides validation of identity information 
              against official South African government databases. The verification process 
              queries the Home Affairs National Identification System (HANIS) and National 
              Population Register (NPR) to confirm the authenticity and current status of 
              the provided identity information.
            </p>

            <p class="text-xs mb-3">
              <strong>Data Sources:</strong> Information is sourced from official government 
              databases maintained by the Department of Home Affairs. Results reflect the 
              most current information available at the time of verification.
            </p>

            <p class="text-xs mb-3">
              <strong>Privacy Notice:</strong> This report contains sensitive personal 
              information protected under the Protection of Personal Information Act (POPIA). 
              Access should be restricted to authorized personnel only.
            </p>

            <div class="text-center text-muted mt-4">
              <p class="mb-0 text-xs">
                © {{ new Date().getFullYear() }} The Good People Data Company. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>
  </PageComponent>
</div>